import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import TSTnBaziResult from "../components/TSTnBaziResult";
import WannianliCalendar from "../components/WannianliCalendar";
import { useLanguage } from "../contexts/LanguageContext";
import { getUserProfile } from "../api/user";
import { DateTime } from "luxon";
import { computeBaziPillars } from "../utils/computeBaziPillars";
import "../styles/main.css";

const STEMS_EN = ["Jia", "Yi", "Bing", "Ding", "Wù", "Ji", "Geng", "Xin", "Ren", "Gui"];
const STEMS_ZH = ["甲", "乙", "丙", "丁", "戊", "己", "庚", "辛", "壬", "癸"];

const getStemName = (val, lang) => {
  if (!val) return '';
  let index = STEMS_EN.indexOf(val);
  if (index === -1) index = STEMS_ZH.indexOf(val);
  if (index === -1) return val;
  return lang === 'zh' ? STEMS_ZH[index] : STEMS_EN[index];
};

const parsePillar = (pillarString) => {
  if (!pillarString || pillarString.length < 2) {
    return { stem: '', branch: '' };
  }
  if (pillarString.includes(' ')) {
    const parenIndex = pillarString.indexOf('(');
    if (parenIndex !== -1) {
      const pinyinPart = pillarString.substring(parenIndex + 1, pillarString.length - 1);
      const pinyinWords = pinyinPart.split(' ');
      const stemPinyin = pinyinWords[0];
      const branchPinyin = pinyinWords[1];
      return { stem: stemPinyin, branch: branchPinyin };
    }
    // Fallback for space separated without parens
    const parts = pillarString.split(' ');
    return { stem: parts[0], branch: parts[1] };
  } else {
    const stem = pillarString.charAt(0);
    const branch = pillarString.charAt(1);
    return { stem, branch };
  }
};

export default function Dashboard({ session }) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [userName, setUserName] = useState("");
  const [baziData, setBaziData] = useState(null);
  const [showBaziModal, setShowBaziModal] = useState(false);
  const [activeTab, setActiveTab] = useState('bazi');
  const [selectedDate, setSelectedDate] = useState(null);

  const calculateBazi = useCallback((dateStr, gender) => {
    if (!dateStr) return null;
    
    let dt = DateTime.fromISO(dateStr, { setZone: true });
    if (!dt.isValid) {
      dt = DateTime.fromSQL(dateStr);
    }

    if (dt.isValid) {
      dt = DateTime.fromObject({
        year: dt.year,
        month: dt.month,
        day: dt.day,
        hour: dt.hour,
        minute: dt.minute,
        second: dt.second
      }, { zone: 'utc' });

      const pillars = computeBaziPillars(dt, language);
      const { stem } = parsePillar(pillars.dayPillar);
      return {
        baziPillars: pillars,
        dayMaster: stem,
        trueSolarTime: dateStr,
        formData: { gender }
      };
    }
    return null;
  }, [language]);

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    const newData = calculateBazi(newDate, baziData?.formData?.gender || "male");
    if (newData) {
      setBaziData(newData);
    }
  };

  useEffect(() => {
    if (session?.user?.email) {
      setUserName(session.user.email.split('@')[0]);
    }
    
    const loadBaziData = async () => {
      let dataToSet = null;

      // 1. Try Profile DOB first
      if (session) {
        try {
          const token = session.access_token;
          const profile = await getUserProfile(token);
          
          if (profile?.name) {
            setUserName(profile.name);
          }

          if (profile?.date_of_birth) {
            const data = calculateBazi(profile.date_of_birth, profile.gender_at_birth || "male");
            if (data) {
              dataToSet = data;
              setSelectedDate(profile.date_of_birth);
            }
          }
        } catch (err) {
          console.warn("Unable to compute BaZi from profile:", err);
        }
      }

      // 2. If no profile DOB (or invalid), try sessionStorage
      if (!dataToSet) {
        const storedData = sessionStorage.getItem('baziResults');
        if (storedData) {
          try {
            const parsed = JSON.parse(storedData);
            let dayMaster = "Unknown";
            if (parsed.baziPillars && parsed.baziPillars.dayPillar) {
               const { stem } = parsePillar(parsed.baziPillars.dayPillar);
               dayMaster = stem; 
            }
            dataToSet = {
              ...parsed,
              dayMaster
            };
            if (parsed.trueSolarTime) {
              setSelectedDate(parsed.trueSolarTime);
            }
          } catch (e) {
            console.error("Failed to parse baziResults", e);
          }
        }
      }

      setBaziData(dataToSet);
    };

    loadBaziData();
  }, [session, language, calculateBazi]);

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #edeff1',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
  };

const toolCardStyle = {
  ...cardStyle, // Inherits basic geometry
  padding: '20px',
  cursor: 'pointer',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  minHeight: '140px',
  // Standardized Glassmorphism for the webpage
  backgroundColor: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  color: '#1c1c1c'
};

  const sectionTitleStyle = {
    fontSize: '1.1rem',
    fontWeight: '700',
    color: '#444',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  return (
    <DashboardLayout session={session}>
      
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        
        {/* Welcome Section */}
        <div style={{ marginBottom: '35px' }}>
          <h1 style={{ fontSize: '2.2rem', fontWeight: '800', color: '#2d3748', marginBottom: '10px', marginTop: 0 }}>
            {language === "en" ? `Welcome back, ${userName}!` : `欢迎回来, ${userName}!`}
          </h1>
          <p style={{ color: '#718096', fontSize: '1.1rem' }}>
            {language === "en" ? "Here is your daily energy overview." : "这是您的每日能量概览。"}
          </p>
        </div>

        {/* Main Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
          gap: '25px',
          marginBottom: '35px'
        }}>
          
          {/* 1. Daily Energy Card */}
          <div style={cardStyle} className="hover-card">
            <div style={sectionTitleStyle}>
              <span style={{ fontSize: '1.4rem' }}>⚡</span>
              {language === "en" ? "Daily Energy" : "今日能量"}
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ 
                background: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)', 
                borderRadius: '16px', 
                padding: '20px', 
                color: 'white',
                marginBottom: '15px',
                boxShadow: '0 4px 15px rgba(253, 160, 133, 0.4)'
              }}>
                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>
                  {baziData ? (baziData.dayMaster ? (language === "en" ? `Day Master: ${getStemName(baziData.dayMaster, language)}` : `日主: ${getStemName(baziData.dayMaster, language)}`) : (language === "en" ? "Chart Ready" : "命盘已就绪")) : (language === "en" ? "No Chart Data" : "暂无命盘")}
                </h3>
                <p style={{ margin: '5px 0 0 0', opacity: 0.9 }}>
                  {new Date().toLocaleDateString(language === 'en' ? 'en-US' : 'zh-CN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
              <p style={{ color: '#666', lineHeight: '1.6' }}>
                {language === "en" 
                  ? "Today is a good day for creative endeavors. Your fire element is strong, suggesting high energy levels." 
                  : "今天是进行创造性活动的好日子。你的火元素很强，表明精力充沛。"}
              </p>
            </div>
          </div>

          {/* 2. Core Identity Card */}
          <div style={cardStyle} className="hover-card">
            <div style={sectionTitleStyle}>
              <span style={{ fontSize: '1.4rem' }}>🧘</span>
              {language === "en" ? "Core Identity" : "核心特质"}
            </div>
            {baziData ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                  <span style={{ color: '#666' }}>{language === "en" ? "Day Master" : "日主"}</span>
                  <span style={{ fontWeight: 'bold', color: '#2d3748' }}>{getStemName(baziData.dayMaster, language) || "-"}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                  <span style={{ color: '#666' }}>{language === "en" ? "Strongest Element" : "最强五行"}</span>
                  <span style={{ fontWeight: 'bold', color: '#e53e3e' }}>{language === "en" ? "Fire 🔥" : "火 🔥"}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                  <span style={{ color: '#666' }}>{language === "en" ? "Lucky Color" : "幸运色"}</span>
                  <span style={{ fontWeight: 'bold', color: '#38a169' }}>{language === "en" ? "Green 🟢" : "绿色 🟢"}</span>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                {language === "en" ? "Loading profile..." : "正在加载..."}
              </div>
            )}
          </div>
        </div>

        {/* Tools Section */}
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#2d3748', marginBottom: '20px' }}>
            {language === "en" ? "Tools & Insights" : "工具与洞察"}
          </h2>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
            gap: '20px' 
          }}>
            {/* Tool: Dream Interpretation */}

            <div style={toolCardStyle} className="hover-card" onClick={() => { setActiveTab('bazi'); setShowBaziModal(true); }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>📜</div>
              <h3 style={{ fontSize: '1.1rem', margin: 0, color: '#333' }}>
                {language === "en" ? "Bazi Chart" : "八字命盘"}
              </h3>
            </div>

            {/* Tool: Major & Annual Luck */}
            <div style={toolCardStyle} className="hover-card" onClick={() => { setActiveTab('dayun'); setShowBaziModal(true); }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>🌊</div>
              <h3 style={{ fontSize: '1.1rem', margin: 0, color: '#333' }}>
                {language === "en" ? "Major & Annual Luck" : "大运流年"}
              </h3>
            </div>

            {/* Tool 2: Chinese Calendar */}
            <div style={toolCardStyle} className="hover-card" onClick={() => { setActiveTab('wannianli'); setShowBaziModal(true); }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>📅</div>
              <h3 style={{ fontSize: '1.1rem', margin: 0, color: '#333' }}>
                {language === "en" ? "Chinese Calendar" : "万年历"}
              </h3>
            </div>

            {/* Tool: Dream Interpretation */}
            <div style={toolCardStyle} className="hover-card" onClick={() => navigate('/dream')}>
              <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>💭</div>
              <h3 style={{ fontSize: '1.1rem', margin: 0, color: '#333' }}>
                {language === "en" ? "Dream Interpretation" : "周公解梦"}
              </h3>
            </div>

            {/* Tool 1 */}
            <div style={toolCardStyle} className="hover-card" onClick={() => alert("Coming Soon!")}>
              <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>☯️</div>
              <h3 style={{ fontSize: '1.1rem', margin: 0, color: '#333' }}>
                {language === "en" ? "I Ching Divination" : "易经占卜"}
              </h3>
            </div>

            {/* Tool 3 */}
            <div style={toolCardStyle} className="hover-card" onClick={() => alert("Coming Soon!")}>
              <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>💑</div>
              <h3 style={{ fontSize: '1.1rem', margin: 0, color: '#333' }}>
                {language === "en" ? "Compatibility" : "合盘分析"}
              </h3>
            </div>

            {/* Tool 4 */}
            <div style={toolCardStyle} className="hover-card" onClick={() => alert("Coming Soon!")}>
              <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>🧭</div>
              <h3 style={{ fontSize: '1.1rem', margin: 0, color: '#333' }}>
                {language === "en" ? "Feng Shui" : "风水指南"}
              </h3>
            </div>

              {/* Tool: AI Face Reading */}
              <div style={toolCardStyle} className="hover-card" onClick={() => alert("Coming Soon!")}>
                <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>🤳</div>
                <h3 style={{ fontSize: '1.1rem', margin: 0, color: '#333' }}>
                  {language === "en" ? "AI Face Reading" : "AI 面相分析"}
                </h3>
              </div>

              {/* Tool: AI Palm Reading */}
              <div style={toolCardStyle} className="hover-card" onClick={() => alert("Coming Soon!")}>
                <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>✋</div>
                <h3 style={{ fontSize: '1.1rem', margin: 0, color: '#333' }}>
                  {language === "en" ? "AI Palm Reading" : "AI 手相分析"}
                </h3>
              </div>

              {/* Tool: Ziwei Purple Star */}
              <div style={toolCardStyle} className="hover-card" onClick={() => alert("Coming Soon!")}>
                <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>🌌</div>
                <h3 style={{ fontSize: '1.1rem', margin: 0, color: '#333' }}>
                  {language === "en" ? "Ziwei Purple Star" : "紫微斗数"}
                </h3>
              </div>

              {/* Tool: Daily Tarot */}
              <div 
                style={toolCardStyle} 
                className="hover-card" 
                onClick={() => { setActiveTab('tarot'); setShowBaziModal(true); }}
              >
                <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>🃏</div>
                <h3 style={{ fontSize: '1.1rem', margin: 0, color: '#333' }}>
                  {language === "en" ? "Tarot of the Day" : "每日塔罗"}
                </h3>
              </div>


              {/* Tool: AI Psychiatrist (Celestial Confidant) */}
              <div style={toolCardStyle} className="hover-card" onClick={() => alert("Coming Soon!")}>
                <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>💎</div>
                <h3 style={{ fontSize: '1.1rem', margin: 0, color: '#333' }}>
                  {language === "en" ? "Stellar Healing" : "心灵知音"}
                </h3>
              </div>

              {/* Tool: MBTI Analysis (Cosmic Signature) */}
              <div style={toolCardStyle} className="hover-card" onClick={() => alert("Coming Soon!")}>
                <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>🧬</div>
                <h3 style={{ fontSize: '1.1rem', margin: 0, color: '#333' }}>
                  {language === "en" ? "Cosmic Personality" : "星际人格"}
                </h3>
              </div>

          </div>
        </div>

      </div>

      {/* Bazi Modal */}
      {showBaziModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 2000, backdropFilter: 'blur(5px)'
        }} onClick={() => setShowBaziModal(false)}>
          <div style={{
            backgroundColor: 'white', borderRadius: '20px',
            width: '90%', maxWidth: '800px', maxHeight: '90vh',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)', position: 'relative',
            display: 'flex', flexDirection: 'column', overflow: 'hidden'
          }} onClick={e => e.stopPropagation()}>
            
            {/* Close Button - Absolute Position */}
            <button 
              onClick={() => setShowBaziModal(false)}
              className="modal-close-btn"
            >
              ×
            </button>

            {/* Modal Header (Fixed) */}
            <div style={{ 
              padding: '20px 50px 15px 20px', // Extra padding right for close button
              borderBottom: '1px solid #eee', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              backgroundColor: '#fff',
              zIndex: 10,
              flexWrap: 'wrap',
              gap: '15px'
            }}>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button 
                    onClick={() => setActiveTab('bazi')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: 'none',
                      backgroundColor: activeTab === 'bazi' ? '#d4af37' : '#f0f0f0',
                      color: activeTab === 'bazi' ? 'white' : '#666',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      transition: 'all 0.2s',
                      width: 'auto',
                      marginBottom: 0
                    }}
                  >
                    {language === "en" ? "Bazi Chart" : "八字命盘"}
                  </button>
                  <button 
                    onClick={() => setActiveTab('dayun')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: 'none',
                      backgroundColor: activeTab === 'dayun' ? '#d4af37' : '#f0f0f0',
                      color: activeTab === 'dayun' ? 'white' : '#666',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      transition: 'all 0.2s',
                      width: 'auto',
                      marginBottom: 0
                    }}
                  >
                    {language === "en" ? "Major & Annual Luck" : "大运流年"}
                  </button>
                  <button 
                    onClick={() => setActiveTab('wannianli')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: 'none',
                      backgroundColor: activeTab === 'wannianli' ? '#d4af37' : '#f0f0f0',
                      color: activeTab === 'wannianli' ? 'white' : '#666',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      transition: 'all 0.2s',
                      width: 'auto',
                      marginBottom: 0
                    }}
                  >
                    {language === "en" ? "Chinese Calendar" : "万年历"}
                  </button>
                </div>
            </div>

            {/* Modal Content (Scrollable) */}
            <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              {activeTab === 'wannianli' ? (
                <WannianliCalendar 
                  dateStr={selectedDate || baziData?.trueSolarTime || DateTime.now().toISO()} 
                  language={language} 
                />
              ) : (
                <TSTnBaziResult 
                  baziPillars={baziData?.baziPillars} 
                  isLoading={false} 
                  language={language} 
                  trueSolarTime={baziData?.trueSolarTime}
                  gender={baziData?.formData?.gender}
                  viewMode={activeTab}
                />
              )}
            </div>

            {/* Footer with Date Picker */}
            <div style={{ 
              padding: '15px 20px', 
              borderTop: '1px solid #eee', 
              backgroundColor: '#f9f9f9',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center'
            }}>
               <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{ fontSize: '0.9rem', color: '#666', whiteSpace: 'nowrap' }}>
                    {language === "en" ? "Browse Date:" : "浏览日期:"}
                  </label>
                  <input 
                    type="datetime-local" 
                    min="1900-01-01T00:00"
                    max="2099-12-31T23:59"
                    value={selectedDate ? DateTime.fromISO(selectedDate).toFormat("yyyy-MM-dd'T'HH:mm") : ''}
                    onChange={handleDateChange}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #ccc',
                      fontSize: '0.9rem',
                      width: 'auto',
                      marginBottom: 0
                    }}
                  />
                </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

